import React, { useEffect, useState } from "react";
import { RESOURCE_TYPE } from "../../enums.js";

/** @typedef {import('../../models/RpaActivityModel.js').RpaActivityItem } RpaActivityItem */
/** @typedef {import('../../services/RpaPlanningService.js').RpaResourcesPerDayObj } RpaResourcesPerDayObj */

/**
 * @typedef {object} RpaReservationStepActivityProps
 * @property {Array<RpaResourcesPerDayObj>} planningDays - Journées du planning
 * @property {?number} selectedActivityId - Activité sélectionnée
 * @property {function(?number):void} onActivityChange - Sélection ou désélection d'une activité
 */

/**
 * Étape de sélection de l'activité
 * @param {RpaReservationStepActivityProps} props
 */
export default function RpaReservationStepActivity({ planningDays, selectedActivityId, onActivityChange }) {
  const [activities, setActivities] = useState(/** @type {Array<RpaActivityItem>} */ ([]));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const displayedActivities = selectedActivityId ? activities.filter(rpaActivity => Number(rpaActivity.id) === Number(selectedActivityId)) : activities;

  useEffect(() => {
    let isMounted = true;

    async function loadActivities() {
      try {
        const rpaActivities = await fetchActivities();

        if (isMounted) {
          setActivities(rpaActivities);
        }
      } catch (fetchError) {
        if (isMounted) {
          setError(fetchError instanceof Error ? fetchError.message : "Impossible de charger les activités.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadActivities();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <section>
      <h3 className="title title--small title--no-margin">Activité</h3>
      <div className="space--16" />
      {loading ? <p className="paragraph paragraph--no-margin paragraph--grey-900">Chargement des activités...</p> : null}
      {error ? (
        <div className="alert alert--danger">
          <span className="material-symbols-outlined">error</span>
          <div className="alert__content">
            <div className="alert__text">{error}</div>
          </div>
        </div>
      ) : null}
      {!loading && !error ? (
        <div className="grid grid--has-6-child grid--8-gap grid--has-2-child-mobile no-mobile">
          {displayedActivities.map(rpaActivity => (
            <button key={rpaActivity.id} type="button" className={`btn btn--full btn--flex-column ${Number(rpaActivity.id) === Number(selectedActivityId) ? "btn--main-color" : "btn--outline-grey"}`} style={{ padding: 0, gap: 0, overflow: "hidden" }} onClick={() => onActivityChange(Number(rpaActivity.id) === Number(selectedActivityId) ? null : Number(rpaActivity.id))}>
              {!selectedActivityId && rpaActivity.thumbnail ? <div style={{ width: "100%", height: "100px", backgroundImage: `url(${rpaActivity.thumbnail})`, backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center", borderRadius: "5px 5px 0 0" }} /> : null}
              <span style={{ display: "flex", flexDirection: "column", gap: "2px", width: "100%", padding: "var(--spacing)" }}>
                <span className="btn__label--bold">{rpaActivity.name}</span>
                {rpaActivity.description ? <span className="btn__label--small">{rpaActivity.description}</span> : null}
                {getResourcesSummary(getResourcesForActivityIds(planningDays, [Number(rpaActivity.id)])) ? <small className="btn__label--small">({getResourcesSummary(getResourcesForActivityIds(planningDays, [Number(rpaActivity.id)]))})</small> : null}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}

/**
 * Retourne les ressources uniques du planning associées à une liste d'activités
 * TU TODO 🔨
 * @param {Array<RpaResourcesPerDayObj>} planningDays - Journées du planning
 * @param {Array<number>} rpaActivityIds - Identifiants des activités
 * @returns {Array<import('../../services/RpaPlanningService.js').ModifiedRpaResourceItem>}
 */
export function getResourcesForActivityIds(planningDays, rpaActivityIds) {
  const resourcesById = new Map();

  planningDays.forEach(day => {
    day.rpaResources.forEach(resource => {
      if (rpaActivityIds.includes(Number(resource.rpaActivityId)) && !resourcesById.has(Number(resource.id))) {
        resourcesById.set(Number(resource.id), resource);
      }
    });
  });

  return [...resourcesById.values()];
}

/**
 * Retourne le résumé du nombre de lieux par type, ex: "2 courts, 1 salle"
 * TU TODO 🔨
 * @param {Array<import('../../services/RpaPlanningService.js').ModifiedRpaResourceItem>} rpaResources - Ressources
 * @returns {string}
 */
export function getResourcesSummary(rpaResources) {
  const countsByType = new Map();

  rpaResources.forEach(resource => {
    const resourceTypeObj = Object.values(RESOURCE_TYPE).find(resourceType => resourceType.value === resource.resource_type) || RESOURCE_TYPE.TERRAIN;
    const countObj = countsByType.get(resourceTypeObj.value) || { resourceTypeObj, count: 0 };

    countObj.count++;
    countsByType.set(resourceTypeObj.value, countObj);
  });

  return [...countsByType.values()].map(({ resourceTypeObj, count }) => `${count} ${count > 1 ? resourceTypeObj.pluralName : resourceTypeObj.singularName}`).join(", ");
}

/**
 * Récupère la liste des activités RPA
 * @returns {Promise<Array<RpaActivityItem>>}
 */
export async function fetchActivities() {
  const response = await fetch("/api/rpa/search/activities", {
    headers: {
      Authorization: "gestiaweb"
    }
  });

  if (!response.ok) {
    throw new Error(`Impossible de charger les activités (${response.status}).`);
  }

  return response.json();
}
